import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash, Download, Upload, User, ChevronLeft, ChevronRight, AlertCircle, CheckCircle, Loader, X } from 'lucide-react';
import ClientWhatsappButton from '../ClientWhatsappButton';
import { useClients } from '../hooks/useClients';

// Componente para el icono de WhatsApp
const BrandWhatsapp = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 24}
      height={props.size || 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
    >
      <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
      <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M9.5 13.5c.5 1 1.5 2 2.5 2s2-1 2.5-2" />
    </svg>
  );
};

const ClientsSection = ({ themes, currentTheme, accounts, services, ...clientsProps }) => {
  const {
    clients,
    loading,
    error,
    showClientForm,
    setShowClientForm,
    showImportClientsModal,
    setShowImportClientsModal,
    importData,
    setImportData,
    importPreview,
    setImportPreview,
    importError,
    setImportError,
    importSuccess,
    setImportSuccess,
    newClient,
    setNewClient,
    currentClientPage,
    itemsPerPage,
    clientSearchTerm,
    setClientSearchTerm,
    fetchClients,
    handleSaveClient,
    handleDeleteClient,
    handleEditClient,
    handleClientFileUpload,
    handleImportClients,
    getPaginatedClients,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange,
    setError
  } = useClients();

  // Estados para el modal de plantillas de WhatsApp
  const [showWhatsappTemplatesModal, setShowWhatsappTemplatesModal] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState('expirado');
  const [templates, setTemplates] = useState({
    expirado: '',
    renovaciones: '',
    cambio_contraseña: '',
    garantia: ''
  });
  const [templateSuccess, setTemplateSuccess] = useState('');

  // Plantillas por defecto
  const defaultTemplates = {
    expirado: `🚨 PERFIL EXPIRADO 🚨
----------------------------
Hola {{nombre_cliente}}, 

Tu perfil de {{servicio}} "{{perfil}}" ha EXPIRADO el {{fecha_expiracion}}.

Para renovar tu suscripción:
💰 Precio: {{precio}}
📱 Contáctanos para reactivar

⚠️ Tu acceso ha sido suspendido
📩 Thend Online - Tu proveedor de confianza`,

    renovaciones: `📅 RECORDATORIO DE RENOVACIÓN 📅
----------------------------
Hola {{nombre_cliente}},

Tu perfil de {{servicio}} "{{perfil}}" vence el {{fecha_expiracion}}.

Detalles de renovación:
💰 Precio: {{precio}}
⏰ Quedan {{dias_restantes}} días
🔄 Renueva ahora para no perder acceso

📩 Thend Online - Siempre contigo`,

    cambio_contraseña: `🔐 CAMBIO DE CONTRASEÑA 🔐
----------------------------
Hola {{nombre_cliente}},

Se ha actualizado la información de acceso de tu perfil "{{perfil}}" en {{servicio}}.

NUEVA INFORMACIÓN:
📧 Usuario: {{usuario}}
🔑 Nueva Contraseña: {{nueva_contraseña}}
👤 Perfil: {{perfil}}
🔢 PIN: {{pin}}

⚠️ Guarda esta información en un lugar seguro
📩 Thend Online - Soporte 24/7`,

    garantia: `🛡️ SOPORTE Y GARANTÍA 🛡️
----------------------------
Hola {{nombre_cliente}},

Estamos aquí para ayudarte con tu servicio {{servicio}}.

Información de tu cuenta:
👤 Perfil: {{perfil}}
📧 Usuario: {{usuario}}
🔢 PIN: {{pin}}
📅 Vence: {{fecha_expiracion}}

🔧 Problemas técnicos: Solucionados en 24h
🔄 Reemplazo de cuenta: Disponible
💬 Soporte: Respuesta inmediata

📩 Thend Online - Tu satisfacción es nuestra prioridad`
  };

  // Cargar plantillas guardadas al iniciar
  useEffect(() => {
    const savedTemplates = localStorage.getItem('whatsapp-notification-templates');
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    } else {
      setTemplates(defaultTemplates);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, []);

  // Función para guardar plantillas
  const handleSaveTemplate = () => {
    localStorage.setItem('whatsapp-notification-templates', JSON.stringify(templates));
    setTemplateSuccess('Plantilla guardada correctamente');
    
    setTimeout(() => {
      setTemplateSuccess('');
    }, 3000);
  };

  // Función para restaurar plantilla por defecto
  const handleRestoreDefaultTemplate = () => {
    setTemplates(prev => ({
      ...prev,
      [selectedTemplateType]: defaultTemplates[selectedTemplateType]
    }));
  };

  // Función para actualizar plantilla
  const handleTemplateChange = (value) => {
    setTemplates(prev => ({
      ...prev,
      [selectedTemplateType]: value
    }));
  };

  // Variables disponibles para las plantillas
  const getAvailableVariables = (templateType) => {
    const baseVariables = [
      '{{nombre_cliente}}',
      '{{servicio}}', 
      '{{perfil}}',
      '{{usuario}}',
      '{{precio}}',
      '{{pin}}',
      '{{telefono}}',
      '{{fecha_expiracion}}'
    ];

    const specificVariables = {
      expirado: ['{{dias_expirado}}'],
      renovaciones: ['{{dias_restantes}}'],
      cambio_contraseña: ['{{nueva_contraseña}}', '{{contraseña_anterior}}'],
      garantia: ['{{tipo_problema}}', '{{fecha_reporte}}']
    };

    return [...baseVariables, ...(specificVariables[templateType] || [])];
  };

  // Función para generar vista previa
  const generatePreview = (templateType) => {
    const template = templates[templateType];
    const sampleData = {
      '{{nombre_cliente}}': 'Juan Pérez',
      '{{servicio}}': 'Netflix Premium',
      '{{perfil}}': 'Perfil 1',
      '{{usuario}}': 'ejemplo@email.com',
      '{{precio}}': '$15.000',
      '{{pin}}': '1234',
      '{{telefono}}': '+57 300 123 4567',
      '{{fecha_expiracion}}': '25/01/2025',
      '{{dias_restantes}}': '3',
      '{{dias_expirado}}': '2',
      '{{nueva_contraseña}}': 'NuevaPass123',
      '{{contraseña_anterior}}': 'ViejaPass456',
      '{{tipo_problema}}': 'Falla de conexión',
      '{{fecha_reporte}}': '20/01/2025'
    };

    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return sampleData[match] || match;
    });
  };

  const getFilteredClients = () => {
    if (!clientSearchTerm) return clients;
    
    return clients.filter(client => {
      const name = client.name.toLowerCase();
      const phone = client.phone?.toLowerCase() || '';
      
      const term = clientSearchTerm.toLowerCase();
      
      return name.includes(term) || phone.includes(term);
    });
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Cargando clientes...</p>
        </div>
      </div>
    );
  }

  if (error && clients.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-2 px-4 py-2 bg-blue-600 rounded-md text-white"
          onClick={() => {
            setError(null);
            fetchClients();
          }}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${themes[currentTheme].textPrimary}`}>Listado de Clientes</h1>
        <div className="flex gap-2">
          <button 
            className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
            onClick={() => setShowImportClientsModal(true)}
          >
            <Download size={20} /> Importar
          </button>
          <button 
            className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
            onClick={() => {
              setNewClient({ name: '', phone: '', type: 'cliente' });
              setShowClientForm(true);
            }}
          >
            <Plus size={20} /> Nuevo
          </button>
          <button 
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            onClick={() => setShowWhatsappTemplatesModal(true)}
          >
            <BrandWhatsapp size={18} />
            <span className="font-medium">Plantillas WhatsApp</span>
          </button>
        </div>
      </div>
      
      {/* Barra de búsqueda */}
      <div className="mb-6">
        <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-lg`}>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Buscar</label>
              <input
                type="text"
                placeholder="Buscar por nombre o teléfono..."
                className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {clients.length === 0 ? (
        <div className={`${themes[currentTheme].bgSecondary} p-8 rounded-lg text-center`}>
          <p className={themes[currentTheme].textMuted}>No hay clientes registrados.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewClient({ name: '', phone: '', type: 'cliente' });
              setShowClientForm(true);
            }}
          >
            Crear el primer cliente
          </button>
        </div>
      ) : (
        <div className={`${themes[currentTheme].bgSecondary} rounded-lg overflow-hidden`}>
          <table className="min-w-full divide-y divide-gray-800">
            <thead className={themes[currentTheme].bgPrimary}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>#</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Nombre</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Teléfono</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Tipo</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Perfiles Activos</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${themes[currentTheme].textSecondary} uppercase tracking-wider`}>Acciones</th>
              </tr>
            </thead>
            <tbody className={`${themes[currentTheme].bgSecondary} divide-y divide-gray-800`}>
              {getPaginatedClients().map((client, index) => {
                // Usa el conteo precargado en lugar de calcularlo
                const activeProfiles = client.active_profiles_count || 0;
                
                const displayIndex = (currentClientPage - 1) * itemsPerPage + index + 1;
                
                return (
                  <tr key={client.id}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{displayIndex}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{client.name}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{client.phone}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>
                      {client.type === 'revendedor' ? (
                        <span className="px-2 py-1 bg-purple-900 text-purple-300 text-xs rounded-full">
                          Revendedor
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded-full">
                          Cliente
                        </span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary}`}>{activeProfiles}</td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${themes[currentTheme].textSecondary} flex gap-2`}>
                      <button 
                        className="text-blue-400 hover:text-blue-500"
                        onClick={() => handleEditClient(client)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-400 hover:text-red-500"
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <Trash size={16} />
                      </button>
                      
                      {/* Botón de WhatsApp para gestionar perfiles del cliente */}
                      <ClientWhatsappButton 
                        client={client} 
                        accounts={accounts}
                        services={services}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Paginación para clientes */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${themes[currentTheme].textMuted}`}>Filas por página:</span>
          <select 
            className={`${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary} rounded-md px-2 py-1 text-sm`}
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div className="flex items-center">
          <span className={`mr-4 text-sm ${themes[currentTheme].textMuted}`}>
            {getFilteredClients().length > 0 
              ? `${(currentClientPage - 1) * itemsPerPage + 1}-${Math.min(currentClientPage * itemsPerPage, getFilteredClients().length)} de ${getFilteredClients().length}` 
              : '0-0 de 0'}
          </span>
          <div className="flex space-x-1">
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${currentClientPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handlePrevPage}
              disabled={currentClientPage === 1}
            >
              <ChevronLeft size={20} className={themes[currentTheme].textMuted} />
            </button>
            <button 
              className={`p-1 rounded-md hover:${themes[currentTheme].bgTertiary} ${currentClientPage * itemsPerPage >= getFilteredClients().length ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleNextPage}
              disabled={currentClientPage * itemsPerPage >= getFilteredClients().length}
            >
              <ChevronRight size={20} className={themes[currentTheme].textMuted} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal para crear/editar cliente */}
      {showClientForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-md`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary}`}>
              {newClient.id ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
            <form className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Nombre</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Teléfono</label>
                <input 
                  type="text" 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themes[currentTheme].textSecondary} mb-1`}>Tipo de Cliente</label>
                <select 
                  className={`w-full p-2 rounded-md ${themes[currentTheme].bgTertiary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary}`}
                  value={newClient.type}
                  onChange={(e) => setNewClient({ ...newClient, type: e.target.value })}
                >
                  <option value="cliente">Cliente</option>
                  <option value="revendedor">Revendedor</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                  onClick={() => {
                    setShowClientForm(false);
                    if (newClient.id) {
                      setNewClient({ name: '', phone: '', type: 'cliente' });
                    }
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveClient}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para importación de clientes */}
      {showImportClientsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto`}>
            <h2 className={`text-xl font-bold mb-4 ${themes[currentTheme].textPrimary} flex items-center`}>
              <Download size={20} className="mr-2 text-blue-400" /> Importar Clientes
            </h2>
            
            <div className="mb-4">
              <p className={`text-sm ${themes[currentTheme].textSecondary} mb-2`}>
                Seleccione un archivo JSON con la lista de clientes. Se requieren los campos name y cellphone.
              </p>
              
              <div className="flex items-center gap-2 mt-4">
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleClientFileUpload}
                  className="hidden" 
                  id="client-file-input" 
                />
                <label 
                  htmlFor="client-file-input"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer flex items-center gap-2"
                >
                  <Upload size={16} /> Seleccionar archivo
                </label>
                {importData && (
                  <span className={`text-sm ${themes[currentTheme].textMuted}`}>
                    {importData.length} cliente(s) encontrado(s)
                  </span>
                )}
              </div>
            </div>
            
            {importError && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{importError}</p>
              </div>
            )}
            
            {importSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{importSuccess}</p>
              </div>
            )}
            
            {importPreview.length > 0 && (
              <div className="mb-4">
                <h3 className={`text-md font-semibold mb-2 ${themes[currentTheme].textPrimary}`}>
                  Vista previa:
                </h3>
                <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-md max-h-64 overflow-y-auto`}>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className={`text-left text-xs ${themes[currentTheme].textMuted} pb-2 px-2`}>Nombre</th>
                        <th className={`text-left text-xs ${themes[currentTheme].textMuted} pb-2 px-2`}>Teléfono</th>
                        <th className={`text-left text-xs ${themes[currentTheme].textMuted} pb-2 px-2`}>Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((client, index) => (
                        <tr key={index} className="border-b border-gray-700">
                          <td className={`text-sm ${themes[currentTheme].textPrimary} py-2 px-2`}>{client.name}</td>
                          <td className={`text-sm ${themes[currentTheme].textPrimary} py-2 px-2`}>
                            {client.cellphone || client.phone || client.cellphone_formatted || 'N/A'}
                          </td>
                          <td className={`text-sm ${themes[currentTheme].textPrimary} py-2 px-2`}>
                            {client.type === 'revendedor' ? (
                              <span className="px-2 py-1 bg-purple-900 text-purple-300 text-xs rounded-full">
                                Revendedor
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded-full">
                                Cliente
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData.length > importPreview.length && (
                    <p className={`text-xs ${themes[currentTheme].textMuted} mt-2`}>
                      ...y {importData.length - importPreview.length} cliente(s) más
                    </p>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <button 
                type="button" 
                className={`px-4 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                onClick={() => {
                  setShowImportClientsModal(false);
                  setImportData(null);
                  setImportPreview([]);
                  setImportError('');
                  setImportSuccess('');
                }}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="px-4 py-2 bg-green-600 rounded-md text-white flex items-center gap-2"
                onClick={handleImportClients}
                disabled={loading || !importData}
              >
                {loading ? (
                  <>
                    <Loader size={16} className="animate-spin" /> Importando...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Importar Clientes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para gestionar plantillas de WhatsApp */}
      {showWhatsappTemplatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themes[currentTheme].bgSecondary} p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${themes[currentTheme].textPrimary} flex items-center`}>
                <BrandWhatsapp size={24} className="mr-3 text-green-400" />
                Gestionar Plantillas de WhatsApp
              </h2>
              <button 
                className={`${themes[currentTheme].textMuted} hover:${themes[currentTheme].textPrimary}`}
                onClick={() => setShowWhatsappTemplatesModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            {templateSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{templateSuccess}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Panel izquierdo: Tipos de plantilla */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${themes[currentTheme].textPrimary}`}>
                  Tipos de Notificación
                </h3>
                
                <div className="space-y-2">
                  {[
                    { key: 'expirado', label: '🚨 Expirado', color: 'bg-red-900 text-red-300' },
                    { key: 'renovaciones', label: '📅 Renovaciones', color: 'bg-orange-900 text-orange-300' },
                    { key: 'cambio_contraseña', label: '🔐 Cambio de Contraseña', color: 'bg-blue-900 text-blue-300' },
                    { key: 'garantia', label: '🛡️ Garantía', color: 'bg-purple-900 text-purple-300' }
                  ].map(({ key, label, color }) => (
                    <button
                      key={key}
                      className={`w-full text-left p-3 rounded-md transition-colors ${
                        selectedTemplateType === key
                          ? `${color} font-semibold`
                          : `${themes[currentTheme].bgSecondary} ${themes[currentTheme].textSecondary} hover:${themes[currentTheme].bgPrimary}`
                      }`}
                      onClick={() => setSelectedTemplateType(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Variables disponibles */}
                <div className="mt-6">
                  <h4 className={`text-sm font-semibold mb-2 ${themes[currentTheme].textSecondary}`}>
                    Variables Disponibles:
                  </h4>
                  <div className="space-y-1">
                    {getAvailableVariables(selectedTemplateType).map(variable => (
                      <div
                        key={variable}
                        className={`text-xs px-2 py-1 ${themes[currentTheme].bgSecondary} rounded font-mono cursor-pointer hover:bg-opacity-70`}
                        onClick={() => {
                          const textarea = document.getElementById('template-textarea');
                          if (textarea) {
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = templates[selectedTemplateType];
                            const newText = text.substring(0, start) + variable + text.substring(end);
                            handleTemplateChange(newText);
                            // Re-focus and set cursor position
                            setTimeout(() => {
                              textarea.focus();
                              textarea.setSelectionRange(start + variable.length, start + variable.length);
                            }, 0);
                          }
                        }}
                        title="Clic para insertar en la plantilla"
                      >
                        {variable}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Panel central: Editor de plantilla */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-semibold ${themes[currentTheme].textPrimary}`}>
                    Editor de Plantilla
                  </h3>
                  <button
                    className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded"
                    onClick={handleRestoreDefaultTemplate}
                  >
                    Restaurar Original
                  </button>
                </div>

                <textarea
                  id="template-textarea"
                  className={`w-full h-80 p-3 rounded-md ${themes[currentTheme].bgSecondary} border ${themes[currentTheme].borderColor} ${themes[currentTheme].textPrimary} font-mono text-sm resize-none`}
                  value={templates[selectedTemplateType]}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  placeholder="Escribe tu plantilla aquí..."
                />

                <div className="mt-4 flex gap-2">
                  <button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                    onClick={handleSaveTemplate}
                  >
                    Guardar Plantilla
                  </button>
                </div>
              </div>

              {/* Panel derecho: Vista previa */}
              <div className={`${themes[currentTheme].bgTertiary} p-4 rounded-lg`}>
                <h3 className={`text-lg font-semibold mb-4 ${themes[currentTheme].textPrimary}`}>
                  Vista Previa
                </h3>
                
                <div className={`${themes[currentTheme].bgSecondary} p-4 rounded-md border-2 border-green-600 min-h-80`}>
                  <div className="flex items-center mb-3 pb-2 border-b border-gray-600">
                    <BrandWhatsapp size={20} className="text-green-400 mr-2" />
                    <span className="text-green-400 font-semibold">WhatsApp</span>
                  </div>
                  
                  <div className={`${themes[currentTheme].textPrimary} whitespace-pre-wrap text-sm leading-relaxed`}>
                    {generatePreview(selectedTemplateType)}
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-400">
                  <p className="mb-2">💡 <strong>Consejos:</strong></p>
                  <ul className="space-y-1 ml-4">
                    <li>• Usa emojis para hacer el mensaje más atractivo</li>
                    <li>• Mantén un tono profesional pero amigable</li>
                    <li>• Incluye información clara y concisa</li>
                    <li>• Haz clic en las variables para insertarlas</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-700">
              <button 
                type="button" 
                className={`px-6 py-2 border ${themes[currentTheme].borderColor} rounded-md ${themes[currentTheme].textSecondary}`}
                onClick={() => setShowWhatsappTemplatesModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsSection;
